import {
  Button,
  Chip,
  Chips,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  RowStyles,
  Tooltip,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import xor from "lodash/xor";
import { Price, priceCell } from "src/components";
import { useFullscreenModal } from "src/components/useFullscreenModal";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidContractType, BidPackageStatus, Maybe, TakeoffLineItemVersion } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { DropCodeCell } from "src/routes/developments/product-offerings/components/DropCodeCell";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { fail, groupBy, isDefined, isNumber, pluralize, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { getOptionsKey } from "./BidsTab";
import { TradeCostData, sumTradeCostData } from "./BidsTab.utils";
import { ImportBidModal } from "./ImportBidModal";
import {
  PlanBasedBidsTable_BidPackageVersionFragment,
  PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment,
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  usePlanBasedBidsTableQuery,
} from "./PlanBasedBidsTable.generated";
import { AddProrationBidModal, EditProrationBidModal } from "./ProrateBidModal";
import { SelectProrationModal } from "./SelectProrationModal";
import { getBidPackageTradesWithEstimate } from "./utils";

type PlanBasedBidsTableProps = {
  bidPackage: PageBidPackageDetailFragment;
  searchFilter?: string;
  requestsWithEditableBids: BidPackageDetailActiveRequestFragment[];
};

export function PlanBasedBidsTable({ bidPackage, searchFilter, requestsWithEditableBids }: PlanBasedBidsTableProps) {
  const [{ versionId }] = useQueryParams({
    versionId: StringParam,
  });

  const query = usePlanBasedBidsTableQuery({
    variables: {
      bidPackageVersionId: versionId ?? bidPackage.latestVersion.id,
      highlightChanges: bidPackage.status.code === BidPackageStatus.Draft && !isDefined(versionId),
    },
  });

  return queryResult(query, ({ bidPackageVersion }) => (
    <PlanBasedBidsTableView
      bidPackage={bidPackage}
      searchFilter={searchFilter}
      requestsWithEditableBids={requestsWithEditableBids}
      bidPackageVersion={bidPackageVersion}
    />
  ));
}

type PlanBasedBidsTableViewProps = PlanBasedBidsTableProps & {
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment;
};

function PlanBasedBidsTableView({
  bidPackage,
  searchFilter,
  requestsWithEditableBids,
  bidPackageVersion,
}: PlanBasedBidsTableViewProps) {
  const { openModal } = useModal();
  const { openFullscreen } = useFullscreenModal();
  const displayingOldVersion = bidPackageVersion.id !== bidPackage.latestVersion.id;

  const tradePartnerIdsWithRevisions = bidPackage.latestVersion.latestBidContractRevisions
    .map((bcr) => bcr.bidContract.tradePartner?.id)
    .compact();
  const requestsWithoutContracts = bidPackage.activeRequests.filter(
    (r) => !tradePartnerIdsWithRevisions.includes(r.tradePartner.id),
  );
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;

  function onCreateBid() {
    if (bidPackage.isUnitBased) {
      openModal({
        content: (
          <ImportBidModal
            requests={requestsWithEditableBids}
            bidPackageName={bidPackage.name}
            latestBidContractRevisions={bidPackage.latestVersion.latestBidContractRevisions}
            bidPackageVersionId={bidPackage.latestVersion.id}
          />
        ),
      });
    } else {
      openModal({
        content: (
          <SelectProrationModal
            requests={requestsWithoutContracts}
            bidPackageName={bidPackage.name}
            onContinue={(tradePartnerId, type) => {
              if (bidPackage.isInternalEstimate) {
                return openFullscreen(
                  <AddProrationBidModal
                    newBidContractParams={{
                      internalEstimateBidPackageId: bidPackage.id,
                      isInternalEstimate: true,
                      type,
                    }}
                    newBidContractRevisionParams={
                      bidPackage.internalEstimateBidContract
                        ? {
                            bidContractId: bidPackage.internalEstimateBidContract.id,
                            bidPackageVersionId: bidPackage.latestVersion.id,
                          }
                        : undefined
                    }
                    scopeLineItems={bidPackageVersion.scopeLines.map((sl) => sl.takeoffLineItemVersion)}
                    tradePartnerName="Homebound"
                  />,
                );
              }
              const request = requestsWithoutContracts.find((r) => r.tradePartner.id === tradePartnerId);
              if (!request) fail("Unable to find request");
              openFullscreen(
                <AddProrationBidModal
                  newBidContractParams={{
                    bidRequestId: request.id,
                    tradePartnerId,
                    type,
                  }}
                  newBidContractRevisionParams={
                    request.bidContract
                      ? { bidContractId: request.bidContract.id, bidPackageVersionId: bidPackage.latestVersion.id }
                      : undefined
                  }
                  scopeLineItems={bidPackageVersion.scopeLines.map((sl) => sl.takeoffLineItemVersion)}
                  tradePartnerName={request.tradePartner.name}
                />,
              );
            }}
            isInternalEstimate={bidPackage.isInternalEstimate}
          />
        ),
      });
    }
  }

  function onEditProratedBid(bidContractRevisionId: string) {
    openFullscreen(
      <EditProrationBidModal
        bidContractRevisionId={bidContractRevisionId}
        scopeLineItems={bidPackageVersion.scopeLines.map((sl) => sl.takeoffLineItemVersion)}
      />,
    );
  }

  const rows = createRows(bidPackage, bidPackageVersion, isDraft);
  const columns = createColumns(bidPackage, onEditProratedBid, isDraft, displayingOldVersion);

  const readyPlanAggregateVersions = bidPackageVersion.readyPlanAggregateVersions;
  const optionTypesGrouped = bidPackageVersion.scopeLines
    .flatMap((scopeLine) => scopeLine.takeoffLineItemVersion.options)
    .unique()
    .groupBy((o) => o.type.id);
  const optionTypesCount = Object.entries(optionTypesGrouped).map(([, options]) => ({
    optionType: options[0].type.name,
    count: options.length,
  }));

  return (
    <>
      <div css={Css.df.w100.aic.gap2.py3.p2.$}>
        <div css={Css.xl.$}>Bid Total</div>
        <Tooltip
          title={
            <>
              {readyPlanAggregateVersions.map((rpav) => (
                <div key={rpav.id}>{rpav.displayName}</div>
              ))}
            </>
          }
          placement="top"
        >
          <Chip
            text={`${readyPlanAggregateVersions.length} ${pluralize(readyPlanAggregateVersions.length, "Offering")}`}
            type="neutral"
          />
        </Tooltip>
        {<Chips values={optionTypesCount.map(({ count, optionType }) => `${count} ${pluralize(count, optionType)}`)} />}
        {!isDraft && !displayingOldVersion && (
          <div css={Css.mla.$}>
            <Button
              variant="text"
              label={bidPackage.isUnitBased ? "Upload Bid" : "Add Bid"}
              disabled={
                (bidPackage.isInternalEstimate &&
                  bidPackage.isUnitBased &&
                  "Currently not supported for Internal Estimates") ||
                ((bidPackage.isInternalEstimate
                  ? bidPackage.latestVersion.latestBidContractRevisions.nonEmpty
                  : (bidPackage.isUnitBased ? requestsWithEditableBids : requestsWithoutContracts).length === 0) &&
                  "Bids cannot be uploaded")
              }
              onClick={onCreateBid}
            />
          </div>
        )}
      </div>
      <GridTable columns={columns} filter={searchFilter} rows={rows} rowStyles={rowStyles} />
    </>
  );
}

const cellBorderStyles = {
  // add border to split trade partner columns
  ...Css.addIn("& > div:nth-of-type(n + 7)", Css.bl.bcGray200.$).$,
};

const rowStyles: RowStyles<Row> = {
  header: { rowCss: cellBorderStyles },
  plan: { rowCss: cellBorderStyles },
  option: { rowCss: cellBorderStyles },
  tliv: { rowCss: cellBorderStyles },
};

function createRows(
  bidPackage: PageBidPackageDetailFragment,
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment,
  isDraft: boolean,
): GridDataRow<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const tradeIds = trades.map((r) => r.tradePartner.id);
  const itemRows = bidPackageVersion.scopeLines.map(({ takeoffLineItemVersion, changeType, previous }) => ({
    kind: "tliv" as const,
    id: takeoffLineItemVersion.id,
    data: {
      item: takeoffLineItemVersion,
      changeType,
      previous,
      // For each materialized scope line, each trade could have bid at most once on it
      costData: trades.keyBy(
        (bpr) => bpr.tradePartner.id,
        ({ bidContractRevision, tradePartner }) => {
          // If we are in draft mode we want to pull costs from the previous version revisions
          if (isDraft) {
            const previous = bidPackageVersion.previous;
            const previousRevision = bidPackage.isInternalEstimate
              ? previous?.latestBidContractRevisions.first
              : previous?.latestBidContractRevisions.find(
                  (bcr) => bcr.bidContract.tradePartner?.id === tradePartner.id,
                );
            if (bidPackage.isUnitBased) {
              const bcli = previousRevision?.lineItems.find(
                (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
              );
              // this is the unit cost on the BCLI
              return isNumber(bcli?.totalCostInCents)
                ? bcli?.totalCostInCents * (takeoffLineItemVersion.quantity || 1)
                : undefined;
            } else {
              /*
                Since we are looking at the previous version to show costs we need to used the identity since there may be a new TLIV
                additionally we want to make sure the options haven't changed between the versions
              */
              const revisionBcli = previousRevision?.lineItems.find(
                (li) =>
                  (li.itemTemplateItem as TakeoffLineItemVersion)?.identity.id === takeoffLineItemVersion.identity.id &&
                  takeoffLineItemVersion.options
                    .map((o) => o.id)
                    .hasSameElements(li.itemTemplateItem?.options.map((o) => o.id) ?? []),
              );
              // These are plan based tli therefore the total cost
              return revisionBcli?.totalCostInCents;
            }
          }
          // if bpr.bidcontract.type is unit based then use the bid contract line item total cost
          if (bidContractRevision?.bidContract?.type?.code === BidContractType.UnitBased) {
            const bcli = bidContractRevision.lineItems.find(
              (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
            );
            // this is the unit cost on the BCLI
            return isNumber(bcli?.totalCostInCents)
              ? bcli?.totalCostInCents * (takeoffLineItemVersion.quantity || 1)
              : undefined;
          }
          // for prorated bids we would want to use the itemtemplateitem cost in cents
          const revisionBcli = bidContractRevision?.lineItems.find(
            (li) => li.itemTemplateItem?.id === takeoffLineItemVersion.id,
          );
          // These are plan based tli therefore the total cost
          return revisionBcli?.totalCostInCents;
        },
      ),
    },
  }));

  // We need to have plan in here to where there are different rows for base plans for each product offering
  const optionGroupBy = groupBy(
    itemRows,
    (i) => i.data.item.readyPlan!.id + (getOptionsKey(i.data.item.options) || "base"),
  );
  const optionRows = Object.keys(optionGroupBy).map((key) => {
    const children = optionGroupBy[key];
    const { item } = children[0].data;
    return {
      kind: "option" as const,
      id: key,
      data: {
        options: item.options,
        costData: children
          // If we are showing removed items, don't sum up their costs
          .filter((c) => c.data.changeType !== AggregateMemberVersionChangeType.REMOVED)
          .map((c) => c.data.costData)
          .reduce(sumTradeCostData.bind(null, tradeIds), {}),
      },
      children,
    };
  });

  const planGroupBy = groupBy(optionRows, (o) => o.children[0].data.item.readyPlan!.id);
  const planRows = Object.keys(planGroupBy).map((key, i) => {
    const children = planGroupBy[key];
    return {
      kind: "plan" as const,
      id: key,
      data: {
        plan: children[0].children[0].data.item.readyPlan!,
        costData: children.map((c) => c.data.costData).reduce(sumTradeCostData.bind(null, tradeIds), {}),
      },
      children: children,
    };
  });
  return [simpleHeader, ...planRows];
}

type HeaderRow = { kind: "header" };
type PlanRow = { kind: "plan"; data: { plan: NamedFragment; costData: TradeCostData } };
type OptionRow = { kind: "option"; data: { options: NamedFragment[]; costData: TradeCostData } };
type TLIVRow = {
  kind: "tliv";
  data: {
    item: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    changeType: Maybe<string>;
    previous: Maybe<PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment>;
    costData: TradeCostData;
  };
};

type Row = HeaderRow | PlanRow | OptionRow | TLIVRow;

function createColumns(
  bidPackage: PageBidPackageDetailFragment,
  onEditProratedBid: (bidContractRevisionId: string) => void,
  isDraft: boolean,
  displayingOldVersion: boolean,
): GridColumn<Row>[] {
  const tradeColumns = getBidPackageTradesWithEstimate(bidPackage);
  return [
    collapseColumn<Row>({
      tliv: (data) => ({
        content: emptyCell.content,
        css: isDraft ? applyStyling(data) : undefined,
      }),
    }),
    column<Row>({
      header: "Code",
      plan: emptyCell,
      option: emptyCell,
      tliv: (data) => ({
        content: <DropCodeCell scope={data.item} />,
        css: isDraft ? applyStyling(data, "drop") : undefined,
      }),
      w: "90px",
    }),
    column<Row>({
      header: "Name",
      plan: ({ plan }) => ({
        content: plan.name,
        css: Css.smSb.$,
      }),
      option: ({ options }) => options.map((o) => o.name).join(", ") || "Base",
      tliv: (data) => ({
        content: data.item.name,
        css: isDraft ? applyStyling(data, "materialVariant") : undefined,
      }),
    }),
    column<Row>({
      header: "Material Code",
      plan: emptyCell,
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.bidItem?.parentMaterialVariant?.code,
        css: isDraft ? applyStyling(data, "materialVariant") : undefined,
      }),
      w: "120px",
    }),
    column<Row>({
      header: "Qty",
      plan: emptyCell,
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.quantity,
        css: isDraft ? applyStyling(data, "quantity") : undefined,
      }),
      w: "53px",
    }),
    column<Row>({
      header: "UoM",
      plan: emptyCell,
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.unitOfMeasure.shortName,
        css: isDraft ? applyStyling(data, "unitOfMeasure") : undefined,
      }),
      w: "67px",
    }),
    ...tradeColumns.map(({ tradePartner, bidContractRevision }) => {
      const isProratedBid =
        bidContractRevision?.bidContract?.type?.code &&
        [BidContractType.GroupedByMaterial, BidContractType.GroupedByPlanAndOption].includes(
          bidContractRevision?.bidContract?.type?.code,
        );

      return numericColumn<Row>({
        header: () => (
          <div css={Css.w100.df.aic.$}>
            <div css={Css.mra.$}>{tradePartner.name}</div>
            {!isDraft && !displayingOldVersion && isProratedBid && (
              <Tooltip title="Edit your prorated bid" placement="top">
                <IconButton
                  icon="estimate"
                  onClick={() => onEditProratedBid(bidContractRevision?.id)}
                  color={Palette.Blue600}
                />
              </Tooltip>
            )}
          </div>
        ),
        plan: ({ costData }) =>
          isNumber(costData[tradePartner.id]) ? (
            <div css={Css.sm.$}>
              <Price valueInCents={costData[tradePartner.id]} />
            </div>
          ) : (
            emptyCell
          ),
        option: ({ costData }) =>
          isNumber(costData[tradePartner.id]) ? priceCell({ valueInCents: costData[tradePartner.id] }) : emptyCell,
        tliv: (data) => ({
          content: isNumber(data.costData[tradePartner.id]) ? (
            <Price valueInCents={data.costData[tradePartner.id]} />
          ) : (
            emptyCell.content
          ),
          css: isDraft ? applyStyling(data) : undefined,
        }),
      });
    }),
  ];
}

type Field = keyof Pick<
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  "options" | "quantity" | "unitOfMeasure" | "materialVariant" | "drop"
>;

function fieldHasChanged(data: TLIVRow["data"], field?: Field): boolean {
  if (!data.previous || !field) return false;

  if (field === "quantity") {
    return data.previous.quantity !== data.item.quantity;
  }

  if (field === "options") {
    return optionsHaveChanged(data);
  }

  const previous = data.previous[field]?.id;
  const current = data.item[field]?.id;

  return previous !== current;
}

function applyStyling(data: TLIVRow["data"], field?: Field) {
  // We want to highlight the whole row if either the row change type is added or if the options on the row have changed
  if (data.changeType === AggregateMemberVersionChangeType.ADDED || fieldHasChanged(data, "options")) {
    return Css.bgYellow50.$;
  }
  if (data.changeType === AggregateMemberVersionChangeType.REMOVED) {
    return Css.bgGray50.tdlt.gray500.$;
  }
  if (data.changeType === AggregateMemberVersionChangeType.UPDATED && fieldHasChanged(data, field)) {
    return Css.bgYellow50.$;
  }
}

function optionsHaveChanged(data: TLIVRow["data"]) {
  const prev = data.previous;
  if (!prev || data.changeType !== AggregateMemberVersionChangeType.UPDATED) return false;
  return xor(
    prev.options?.map((o) => o.id),
    data.item.options?.map((o) => o.id),
  ).nonEmpty;
}
